/**
 * OnboardingContext.js
 *
 * Provides a context and provider for managing the state of the onboarding wizard.
 * Handles data collection, navigation between steps, and persistence to the backend.
 */
import TagTypes from '@/constants/TagTypes';
import usePotentialSites from '@/hooks/usePotentialSites';
import useUserTags from '@/hooks/useUserTags';
import storage, { SCHOOL_DRAFT_KEY } from '@/utils/storage';
import { useUser } from '@lore/auth';
import { useConnect, useQueryMutation } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const OnboardingContext = createContext(null);

OnboardingProvider.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  onComplete: PropTypes.func.isRequired,
  children: PropTypes.node,
};

/**
 * OnboardingProvider.js
 *
 * Context provider for the onboarding wizard.
 *
 * @param {object} props - Component props.
 * @param {Array} props.steps - The sequence of steps in the wizard.
 * @param {Function} props.onComplete - Callback when the wizard is finished.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The rendered provider.
 */
export function OnboardingProvider(props) {
  const { steps, onComplete, children } = props;
  const currentUser = useUser();
  const queryClient = useQueryClient();

  // Fetch user subjects and expertise tags
  const userSubjects = useUserTags(currentUser, TagTypes.Subject);
  const userExpertise = useUserTags(currentUser, TagTypes.Expertise);

  // Fetch roles associated with the user
  const roles = useConnect('role.find', {
    where: {
      eager: {
        $where: {
          userId: currentUser.data.id
        }
      }
    },
    pagination: {
      page: 1,
      pageSize: 1,
      order: 'createdAt desc'
    }
  }, [currentUser.data.id]);

  const siteId = roles.data?.[0]?.data?.siteId;

  // Fetch site information if a siteId is found in the user's roles
  const sites = useConnect('site.find', {
    where: {
      eager: {
        $where: {
          id: siteId
        }
      }
    },
    pagination: {
      page: 1,
      pageSize: 1,
      order: 'createdAt asc'
    }
  }, {
    enabled: !!siteId
  });

  const potentialSites = usePotentialSites({
    pageSize: 1,
  });

  // Build a map from the step id → step node for O(1) lookup
  const stepsMap = useMemo(
    () => Object.fromEntries(steps.map((s) => [s.id, s])),
    [steps]
  );

  const [history, setHistory] = useState([steps[0].id]);
  const [data, setData] = useState(() => {
    const updatedUserData = _.pick(currentUser.data, [
      'id',
      'firstName',
      'lastName',
      'phoneNumber',
      'zipCode',
      'latitude',
      'longitude',
      'intents'
    ]);
    const selectedIntents = (updatedUserData.intents || []).map(i => {
      if (i.name === 'Educator') return 'school_educator';
      if (i.name === 'Organizer') return 'org_educator';
      if (i.name === 'Partner') return 'community_partner';
      return null;
    }).filter(Boolean);

    const site = currentUser.data.sites?.[0];
    const potentialSite = currentUser.data.potentialSites?.[0];

    let siteData = {
      siteId: '',
      name: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      city: '',
      state: '',
      zipCode: updatedUserData.zipCode || '',
      title: '',
      type: 'Custom'
    };

    if (site) {
      siteData = {
        ...siteData,
        siteId: site.id,
        name: site.name || '',
        addressLine1: site.addressLine1 || '',
        addressLine2: site.addressLine2 || '',
        city: site.city || '',
        state: site.state || '',
        zipCode: site.zipCode || '',
        type: 'Site'
      };
    } else if (potentialSite) {
      siteData = {
        ...siteData,
        siteId: potentialSite.id,
        name: potentialSite.name || '',
        addressLine1: potentialSite.addressLine1 || '',
        addressLine2: potentialSite.addressLine2 || '',
        city: potentialSite.city || '',
        state: potentialSite.state || '',
        zipCode: potentialSite.zipCode || '',
        type: 'PotentialSite'
      };
    }

    return {
      ...updatedUserData,
      selectedIntents,
      userSubjects: userSubjects.data,
      userExpertise: userExpertise.data,
      roles: roles.data,
      siteData
    }
  });
  const [stepNextDisabled, setNextDisabled] = useState(false);

  const onBeforeNextRef = useRef(null);
  // Always holds the latest data so async goNext can read it after awaiting
  // onBeforeNextRef (which may call updateData before resolving).
  const dataRef = useRef(data);

  const userMutation = useQueryMutation('currentUser.updateSingleton', currentUser);
  const tagNodeCreateMutation = useQueryMutation('tagNode.create');
  const tagNodeUpdateMutation = useQueryMutation('tagNode.updateDynamic');
  const tagNodeDestroyMutation = useQueryMutation('tagNode.destroyDynamic');
  const roleCreateMutation = useQueryMutation('role.create');
  const { saving, error, request } = userMutation;

  const [isRefetching, setIsRefetching] = useState(false);

  const refetchUser = useCallback(async () => {
    setIsRefetching(true);
    // This function returns a promise that will resolve when all the queries are done being refetched.
    await queryClient.invalidateQueries();
    setIsRefetching(false);
  }, [queryClient]);

  const currentStep = history[history.length - 1];
  const activeStep = stepsMap[currentStep];
  const isFirst = history.length === 1;

  const isLast = useMemo(() => {
    if (!activeStep) return true;
    if (typeof activeStep.next === 'function') {
      return !activeStep.next(data);
    }
    return !activeStep.next;
  }, [activeStep, data]);

  // Reset the step-specific state when the step changes
  useEffect(() => {
    onBeforeNextRef.current = null;
    userMutation.reset();
  }, [currentStep]);

  const userSubjectsKey = [
    userSubjects.state,
    ...(userSubjects.data || []).map(s => `${s.id}:${s._tagNode?.data?.description || ''}`)
  ].join('|');

  useEffect(() => {
    if (userSubjects.state === PayloadStates.FETCHING) return
    setData((prev) => {
      const next = { ...prev, userSubjects: userSubjects.data };
      dataRef.current = next;
      return next;
    });
  }, [userSubjectsKey]);

  const userExpertiseKey = [
    userExpertise.state,
    ...(userExpertise.data || []).map(s => `${s.id}:${s._tagNode?.data?.description || ''}`)
  ].join('|');

  useEffect(() => {
    if (userExpertise.state === PayloadStates.FETCHING) return
    setData((prev) => {
      const next = { ...prev, userExpertise: userExpertise.data };
      dataRef.current = next;
      return next;
    });
  }, [userExpertiseKey]);

  const userRolesKey = [
    roles.state,
    ...(roles.data || []).map(r => `${r.id}`)
  ].join('|');
  useEffect(() => {
    if (roles.state === PayloadStates.FETCHING) return
    setData((prev) => {
      const firstRole = roles.data?.[0];
      const siteId = firstRole?.data?.siteId;

      const next = {
        ...prev,
        roles: roles.data,
        siteData: siteId ? { ...prev.siteData, siteId } : prev.siteData
      };
      dataRef.current = next;
      return next;
    });
  }, [userRolesKey]);

  const userSiteKey = [
    roles.state, sites.state, roles.data?.[0]?.data?.siteId,
    ...(sites.data || []).map(s => `${s.id}`)
  ].join('|');
  useEffect(() => {
    if (sites.state === PayloadStates.FETCHING || !sites.data?.[0]) return
    if (sites.data[0].id !== siteId) return
    setData((prev) => {
      const site = sites.data[0];
      const next = {
        ...prev,
        siteData: {
          ...prev.siteData,
          siteId: site.id,
          name: site.data.name || '',
          addressLine1: site.data.addressLine1 || '',
          addressLine2: site.data.addressLine2 || '',
          addressLine3: site.data.addressLine3 || '',
          city: site.data.city || '',
          state: site.data.state || '',
          zipCode: site.data.zipCode || prev.zipCode || '',
          isActive: site.data.isActive ?? false,
          type: 'Site'
        }
      };
      dataRef.current = next;
      return next;
    });
  }, [userSiteKey]);

  const userPotentialSiteKey = [
    potentialSites.isLoading,
    ...(potentialSites.data || []).map(s => `${s.id}`)
  ].join('|');
  useEffect(() => {
    if (potentialSites.isLoading || !potentialSites.data?.[0]) return
    // Only use potential site data if we don't already have site data from a role
    if (siteId) return
    setData((prev) => {
      const site = potentialSites.data[0];
      const next = {
        ...prev,
        siteData: {
          ...prev.siteData,
          siteId: site.id,
          name: site.name || '',
          addressLine1: site.addressLine1 || '',
          addressLine2: site.addressLine2 || '',
          city: site.city || '',
          state: site.state || '',
          zipCode: site.zipCode || prev.zipCode || '',
          type: 'PotentialSite'
        }
      };
      dataRef.current = next;
      return next;
    });
  }, [userPotentialSiteKey, siteId]);

  useEffect(() => {
    const updatedUserData = _.pick(currentUser.data, [
      'id',
      'firstName',
      'lastName',
      'phoneNumber',
      'zipCode',
      'latitude',
      'longitude',
      'intents'
    ]);
    const selectedIntentsFromBackend = (updatedUserData.intents || []).map(i => {
      if (i.name === 'Educator') return 'school_educator';
      if (i.name === 'Organizer') return 'org_educator';
      if (i.name === 'Partner') return 'community_partner';
      return null;
    }).filter(Boolean);

    setData((prev) => {
      const next = {
        ...prev,
        ...updatedUserData,
        // Only update selectedIntents if we don't already have some wizard-local choices?
        // Actually, if we just refetched, we want the latest from backend,
        // but we should probably preserve wizard-local choices if we are on the intent step.
        // For now, let's just use what's there unless it's missing.
        selectedIntents: prev.selectedIntents?.length > 0 ? prev.selectedIntents : selectedIntentsFromBackend
      };
      dataRef.current = next;
      return next;
    });
  }, [currentUser.data]);

  const updateData = useCallback((patch) => {
    dataRef.current = { ...dataRef.current, ...patch };
    setData((prev) => ({ ...prev, ...patch }));
  }, []);

  const save = useCallback(async (stepData) => {
    try {
      updateData(stepData);
      await userMutation.mutateAsync(stepData);
      return true;
    } catch {
      return false;
    }
  }, [userMutation.mutateAsync, updateData]);

  const registerOnBeforeNext = useCallback((fn) => {
    onBeforeNextRef.current = fn;
  }, []);

  const goNext = useCallback(async () => {
    if (onBeforeNextRef.current) {
      const canProceed = await onBeforeNextRef.current();
      if (!canProceed) return;
    }
    // Read from the ref, not the closed-over `data`, so we always have the
    // value written by onBeforeNext (e.g. updateData) even though React
    // hasn't re-rendered and refreshed the closure yet.
    const currentData = dataRef.current;
    if (!activeStep?.next) {
      await refetchUser();
      onComplete?.(currentData);
      return;
    }
    const nextId = typeof activeStep.next === 'function' ? activeStep.next(currentData) : activeStep.next;
    if (nextId) {
      setHistory((h) => [...h, nextId]);
    } else {
      await refetchUser();
      onComplete?.(currentData);
    }
  }, [activeStep, onComplete, refetchUser]);

  const goBack = useCallback(() => {
    setHistory((h) => (h.length > 1 ? h.slice(0, -1) : h));
  }, []);

  const isLoading = useMemo(() =>
      isRefetching ||
      userSubjects.state === PayloadStates.FETCHING ||
      userExpertise.state === PayloadStates.FETCHING ||
      roles.state === PayloadStates.FETCHING ||
      (!!siteId && sites.state === PayloadStates.FETCHING),
    [isRefetching, userSubjects.state, userExpertise.state, roles.state, siteId, sites.state]);

  const roleCreate = useCallback(async (payload) => {
    const result = await roleCreateMutation.mutateAsync(payload);
    storage.delete(SCHOOL_DRAFT_KEY);
    return result;
  }, [roleCreateMutation.mutateAsync]);

  const isMutationSaving = saving || tagNodeCreateMutation.saving || tagNodeUpdateMutation.saving || tagNodeDestroyMutation.saving || roleCreateMutation.saving;

  const value = useMemo(() => ({
    // Navigation state
    currentStep,
    isFirst,
    isLast,
    // Step metadata
    steps,
    stepsMap,
    activeStep,
    history,
    completeSteps: history.slice(0, -1),
    // Collected data
    data,
    updateData,
    // Navigation actions
    goNext,
    goBack,
    refetchUser,
    // Mutation state (exposed for steps to read)
    loading: isLoading,
    saving: isMutationSaving,
    error,
    request,
    save,
    tagNodeCreate: tagNodeCreateMutation.mutateAsync,
    tagNodeUpdate: tagNodeUpdateMutation.mutateAsync,
    tagNodeDestroy: tagNodeDestroyMutation.mutateAsync,
    roleCreate,
    // Step control
    nextDisabled: isMutationSaving || stepNextDisabled,
    setNextDisabled,
    registerOnBeforeNext,
  }), [
    currentStep, isFirst, isLast, steps, stepsMap, activeStep, history,
    data, updateData, goNext, goBack, refetchUser,
    isLoading, isMutationSaving, error, request, save,
    tagNodeCreateMutation.mutateAsync, tagNodeUpdateMutation.mutateAsync, tagNodeDestroyMutation.mutateAsync,
    roleCreate, stepNextDisabled, setNextDisabled, registerOnBeforeNext,
  ]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

/**
 * Custom hook to access the onboarding context.
 *
 * @returns {object} The onboarding context value.
 */
export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within <OnboardingProvider>");
  }
  return ctx;
}
